import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { FlowContactField, FlowContactsReport, FlowPage, FlowPreference } from '../../../models/preference-flow-report.model';

/**
 * Angular service for the n8n-style 3-node Preference Flow Report API.
 * Uses HttpClient directly (not BaseApiService) because the endpoints are non-standard:
 * complex query params, blob response for export.
 *
 * Registered in APIService facade as `this.preferenceFlowReport`.
 */
@Injectable()
export class PreferenceFlowReportService {
  private readonly baseUrl: string;

  public constructor(private readonly httpClient: HttpClient) {
    this.baseUrl = environment.api.API_ENDPOINT;
  }

  /**
   * Downloads all contacts for a preference as an Excel (.xlsx) blob.
   *
   * @param prefId  The MongoDB ObjectId of the CorePreference.
   * @param businessUnit Optional business unit filter.
   */
  public exportExcel(prefId: string, businessUnit?: string): Observable<Blob> {
    const params: Record<string, string> = {};
    if (businessUnit) {
      params['businessUnit'] = businessUnit;
    }
    return this.httpClient.get(
      `${this.baseUrl}/preferenceflowreport/preferences/${prefId}/export`,
      { params, responseType: 'blob' }
    );
  }

  /**
   * Fetches paginated contacts + value distribution for Node 3 via a contact field (Node 2b).
   *
   * @param fieldId   The MongoDB ObjectId of the CoreContactField.
   * @param search    Email address search string (pass empty string for no filter).
   * @param page      1-based page number.
   * @param pageSize  Number of items per page.
   */
  public getContactFieldContacts(
    fieldId: string,
    search: string,
    page: number,
    pageSize: number
  ): Observable<FlowContactsReport> {
    const params: Record<string, string> = {
      page: String(page),
      pageSize: String(pageSize),
      search,
    };
    return this.httpClient.get<FlowContactsReport>(
      `${this.baseUrl}/preferenceflowreport/contactfields/${fieldId}/contacts`,
      { params }
    );
  }

  /**
   * Fetches CoreContactFields linked to a page with contact count badges for Node 2b.
   *
   * @param pageId  The MongoDB ObjectId of the PreferencePage.
   */
  public getContactFields(pageId: string): Observable<FlowContactField[]> {
    return this.httpClient.get<FlowContactField[]>(
      `${this.baseUrl}/preferenceflowreport/pages/${pageId}/contactfields`
    );
  }

  /**
   * Fetches paginated contacts + value distribution for Node 3.
   *
   * @param prefId    The MongoDB ObjectId of the CorePreference.
   * @param search    Email address search string (pass empty string for no filter).
   * @param page      1-based page number.
   * @param pageSize  Number of items per page.
   * @param businessUnit Optional business unit filter.
   */
  public getContacts(
    prefId: string,
    search: string,
    page: number,
    pageSize: number,
    businessUnit?: string
  ): Observable<FlowContactsReport> {
    const params: Record<string, string> = {
      page: String(page),
      pageSize: String(pageSize),
      search,
    };
    if (businessUnit) {
      params['businessUnit'] = businessUnit;
    }
    return this.httpClient.get<FlowContactsReport>(
      `${this.baseUrl}/preferenceflowreport/preferences/${prefId}/contacts`,
      { params }
    );
  }

  /**
   * Fetches all preference pages for Node 1, optionally filtered by business unit.
   *
   * @param businessUnit Optional business unit filter.
   */
  public getPages(businessUnit?: string): Observable<FlowPage[]> {
    const params: Record<string, string> = {};
    if (businessUnit) {
      params['businessUnit'] = businessUnit;
    }
    return this.httpClient.get<FlowPage[]>(
      `${this.baseUrl}/preferenceflowreport/pages`,
      { params }
    );
  }

  /**
   * Fetches CorePreferences linked to a page with contact count badges for Node 2.
   *
   * @param pageId  The MongoDB ObjectId of the PreferencePage.
   */
  public getPreferences(pageId: string): Observable<FlowPreference[]> {
    return this.httpClient.get<FlowPreference[]>(
      `${this.baseUrl}/preferenceflowreport/pages/${pageId}/preferences`
    );
  }
}
